import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Camera, ArrowRight, Check, Shield, Zap, Users } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/App";

export default function Onboarding() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [animating, setAnimating] = useState(false);
  const fileInputRef = useRef(null);
  
  // Profile data
  const [profilePicture, setProfilePicture] = useState(null);
  const [profilePictureFile, setProfilePictureFile] = useState(null);
  const [bio, setBio] = useState("");
  const [age, setAge] = useState("");
  const [city, setCity] = useState("");
  const [phone, setPhone] = useState("");
  
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error("Image too large (max 5MB)");
        return;
      }
      setProfilePictureFile(file);
      const reader = new FileReader();
      reader.onload = (e) => setProfilePicture(e.target.result);
      reader.readAsDataURL(file);
    }
  };
  
  const handleNext = async () => {
    if (step < 3) {
      setAnimating(true);
      setTimeout(() => {
        setStep(prev => prev + 1);
        setAnimating(false);
      }, 300);
      return;
    }
    
    // Final step - save profile
    setLoading(true);
    try {
      if (profilePictureFile) {
        const formData = new FormData();
        formData.append('file', profilePictureFile);
        await api.post('/upload/profile-picture', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }
      
      await api.put('/profile', {
        bio: bio || null,
        age: age ? parseInt(age) : null,
        city: city || null,
        phone: phone || null
      });
      
      await api.post('/profile/complete-onboarding');
      
      try {
        await api.post('/demo/create-nearby-users');
      } catch (e) {}
      
      toast.success("Benvenuto su ADE");
      navigate('/app/home', { replace: true });
    } catch (error) {
      toast.error("Error saving profile");
    } finally {
      setLoading(false);
    }
  };
  
  const handleBack = () => {
    if (step > 1) {
      setAnimating(true);
      setTimeout(() => {
        setStep(prev => prev - 1);
        setAnimating(false);
      }, 300);
    }
  };
  
  // Step 1 - "Here, Now." with powerful elite design
  const renderStep1 = () => (
    <div className={`flex-1 flex flex-col items-center justify-center px-8 transition-all duration-500 ${animating ? 'opacity-0 scale-95' : 'opacity-100 scale-100'}`}>
      {/* Main content */}
      <div className="relative z-10 text-center">
        {/* ADE Logo */}
        <div className="relative mb-8">
          <div className="absolute inset-0 blur-3xl bg-gradient-to-r from-orange-500/40 via-pink-500/40 to-purple-500/40 scale-150" />
          <div className="relative w-44 h-28 mx-auto flex items-center justify-center">
            <img 
              src="/images/ade-logo-extracted.png" 
              alt="ADE" 
              className="max-w-full max-h-full object-contain drop-shadow-2xl"
              style={{ filter: 'drop-shadow(0 0 30px rgba(255, 100, 150, 0.5))' }}
            />
          </div>
        </div>
        
        <h1 className="text-5xl sm:text-6xl font-extralight text-white mt-6 mb-6 tracking-tight">
          Here, Now.
        </h1>
        
        <p className="text-white/40 text-lg max-w-sm mx-auto leading-relaxed font-light">
          Connect with people around you.<br />
          In real time.
        </p>
      </div>
    </div>
  );
  
  // Step 2 - Power features
  const renderStep2 = () => (
    <div className={`flex-1 flex flex-col items-center justify-center px-8 transition-all duration-500 ${animating ? 'opacity-0 scale-95' : 'opacity-100 scale-100'}`}>
      <div className="relative z-10 w-full max-w-sm">
        {/* Logo smaller */}
        <div className="flex justify-center mb-8">
          <div className="w-28 h-16 flex items-center justify-center">
            <img 
              src="/images/ade-logo-extracted.png" 
              alt="ADE" 
              className="max-w-full max-h-full object-contain drop-shadow-xl"
              style={{ filter: 'drop-shadow(0 0 15px rgba(255, 100, 150, 0.4))' }}
            />
          </div>
        </div>
        
        <div className="space-y-4">
          {/* Feature 1 - Security */}
          <div className="flex items-center gap-4 bg-white/[0.03] backdrop-blur-md rounded-2xl p-5 border border-white/[0.08] hover:border-orange-500/30 transition-all group">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center flex-shrink-0 shadow-lg shadow-orange-500/30 group-hover:scale-110 transition-transform">
              <Shield className="w-7 h-7 text-white" />
            </div>
            <div>
              <h3 className="text-white font-semibold text-lg">Verified & Secure</h3>
              <p className="text-white/40 text-sm">Golden Ring trust system</p>
            </div>
          </div>
          
          {/* Feature 2 - Power */}
          <div className="flex items-center gap-4 bg-white/[0.03] backdrop-blur-md rounded-2xl p-5 border border-white/[0.08] hover:border-pink-500/30 transition-all group">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-pink-500 to-pink-600 flex items-center justify-center flex-shrink-0 shadow-lg shadow-pink-500/30 group-hover:scale-110 transition-transform">
              <Zap className="w-7 h-7 text-white" />
            </div>
            <div>
              <h3 className="text-white font-semibold text-lg">Instant Connection</h3>
              <p className="text-white/40 text-sm">Real-time proximity discovery</p>
            </div>
          </div>
          
          {/* Feature 3 - Network */}
          <div className="flex items-center gap-4 bg-white/[0.03] backdrop-blur-md rounded-2xl p-5 border border-white/[0.08] hover:border-purple-500/30 transition-all group">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center flex-shrink-0 shadow-lg shadow-purple-500/30 group-hover:scale-110 transition-transform">
              <Users className="w-7 h-7 text-white" />
            </div>
            <div>
              <h3 className="text-white font-semibold text-lg">Exclusive Network</h3>
              <p className="text-white/40 text-sm">Premium social connections</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
  
  // Step 3 - Profile completion
  const renderStep3 = () => (
    <div className={`flex-1 px-6 py-8 overflow-y-auto bg-white transition-all duration-500 ${animating ? 'opacity-0 scale-95' : 'opacity-100 scale-100'}`}>
      <div className="max-w-sm mx-auto">
        <h2 className="text-2xl font-bold text-gray-900 mb-2 text-center">
          Your Profile
        </h2>
        <p className="text-gray-500 text-center mb-8 text-sm">
          Let others know who you are
        </p>
        
        {/* Profile Picture */}
        <div className="flex justify-center mb-8">
          <div className="relative">
            <Avatar className="w-28 h-28 border-4 border-white shadow-2xl">
              {profilePicture ? (
                <AvatarImage src={profilePicture} alt="Profile" />
              ) : (
                <AvatarFallback className="bg-gradient-to-br from-orange-100 via-pink-100 to-purple-100 text-purple-400 text-3xl font-medium">
                  ?
                </AvatarFallback>
              )}
            </Avatar>
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="absolute bottom-0 right-0 w-10 h-10 rounded-full bg-gradient-to-r from-orange-500 via-pink-500 to-purple-500 text-white flex items-center justify-center shadow-lg hover:scale-110 transition-transform"
              data-testid="upload-photo-btn"
            >
              <Camera className="w-5 h-5" />
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileSelect}
              className="hidden"
            />
          </div>
        </div>
        
        {/* Form Fields */}
        <div className="space-y-5">
          <div className="space-y-2">
            <Label htmlFor="bio" className="text-sm font-medium text-gray-700">Bio</Label>
            <Textarea
              id="bio"
              placeholder="Tell us about yourself..."
              value={bio}
              onChange={(e) => setBio(e.target.value)}
              className="min-h-[90px] rounded-xl border-gray-200 bg-gray-50/50 focus:bg-white focus:ring-2 focus:ring-pink-500/20 resize-none transition-all"
              maxLength={150}
              data-testid="bio-input"
            />
            <p className="text-xs text-gray-400 text-right">{bio.length}/150</p>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="age" className="text-sm font-medium text-gray-700">Age</Label>
              <Input
                id="age"
                type="number"
                placeholder="25"
                value={age}
                onChange={(e) => setAge(e.target.value)}
                className="h-12 rounded-xl border-gray-200 bg-gray-50/50 focus:bg-white focus:ring-2 focus:ring-pink-500/20 transition-all"
                min="18"
                max="99"
                data-testid="age-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="city" className="text-sm font-medium text-gray-700">City</Label>
              <Input
                id="city"
                type="text"
                placeholder="Rome"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                className="h-12 rounded-xl border-gray-200 bg-gray-50/50 focus:bg-white focus:ring-2 focus:ring-pink-500/20 transition-all"
                data-testid="city-input"
              />
            </div>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="phone" className="text-sm font-medium text-gray-700">Phone (optional)</Label>
            <Input
              id="phone"
              type="tel"
              placeholder="+1 555 1234567"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className="h-12 rounded-xl border-gray-200 bg-gray-50/50 focus:bg-white focus:ring-2 focus:ring-pink-500/20 transition-all"
              data-testid="phone-input"
            />
            <p className="text-xs text-gray-400">Won't be publicly visible</p>
          </div>
        </div>
      </div>
    </div>
  );
  
  return (
    <div className="min-h-screen flex flex-col overflow-hidden" data-testid="onboarding-page">
      {/* Premium elite dark background for steps 1-2 */}
      {step < 3 && (
        <div className="absolute inset-0 bg-black">
          {/* Base gradient */}
          <div className="absolute inset-0 bg-gradient-to-b from-[#0a0510] via-[#0d0815] to-black" />
          
          {/* Radial gradient spotlight from top */}
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[600px] bg-gradient-radial from-purple-900/30 via-purple-900/10 to-transparent opacity-60" />
          
          {/* Accent glow orbs */}
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-orange-600/10 rounded-full blur-[100px] animate-pulse-slow" />
          <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-pink-600/10 rounded-full blur-[80px] animate-pulse-slow" style={{ animationDelay: '1s' }} />
          <div className="absolute top-1/2 right-1/3 w-64 h-64 bg-purple-600/8 rounded-full blur-[60px] animate-pulse-slow" style={{ animationDelay: '2s' }} />
          
          {/* Subtle noise texture */}
          <div className="absolute inset-0 opacity-[0.015]" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`
          }} />
          
          {/* Vertical light beam accent */}
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-px h-40 bg-gradient-to-b from-orange-500/50 via-pink-500/20 to-transparent" />
          
          {/* Bottom ambient glow */}
          <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-purple-900/20 to-transparent" />
          
          {/* Corner accents */}
          <div className="absolute top-0 left-0 w-32 h-32 bg-gradient-to-br from-orange-500/5 to-transparent" />
          <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-pink-500/5 to-transparent" />
        </div>
      )}
      
      {/* Progress bar */}
      <div className={`relative z-50 px-6 pt-6 pb-4 ${step === 3 ? 'bg-white' : ''}`}>
        <div className="flex gap-2 max-w-sm mx-auto">
          {[1, 2, 3].map((s) => (
            <div
              key={s}
              className={`h-1 flex-1 rounded-full transition-all duration-500 ${
                s <= step 
                  ? 'bg-gradient-to-r from-orange-500 via-pink-500 to-purple-500' 
                  : step === 3 ? 'bg-gray-200' : 'bg-white/10'
              }`}
            />
          ))}
        </div>
      </div>
      
      {/* Content */}
      <div className="relative flex-1 flex flex-col">
        {step === 1 && renderStep1()}
        {step === 2 && renderStep2()}
        {step === 3 && renderStep3()}
      </div>
      
      {/* Footer */}
      <div className={`onb-footer relative z-50 p-6 pb-10 ${step === 3 ? 'bg-white border-t border-gray-100' : ''}`}>
        <div className="max-w-sm mx-auto flex gap-3">
          {step > 1 && (
            <Button
              type="button"
              variant="outline"
              onClick={handleBack}
              disabled={animating}
              className={`flex-1 h-14 rounded-2xl text-base font-medium transition-all ${
                step === 3 
                  ? 'border-gray-200 hover:bg-gray-50' 
                  : 'border-white/10 text-white/70 hover:text-white hover:bg-white/5 bg-transparent'
              }`}
              data-testid="back-btn"
            >
              Back
            </Button>
          )}
          <Button
            type="button"
            onClick={handleNext}
            disabled={loading || animating}
            className="flex-1 h-14 rounded-2xl bg-gradient-to-r from-orange-500 via-pink-500 to-purple-600 text-white text-base font-semibold hover:opacity-90 shadow-xl shadow-pink-500/25 border-0 transition-all hover:scale-[1.02] active:scale-[0.98]"
            data-testid="next-btn"
          >
            {loading ? (
              <span className="flex items-center gap-2">
                <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Saving...
              </span>
            ) : step < 3 ? (
              <span className="flex items-center gap-2">
                Continue
                <ArrowRight className="w-5 h-5" />
              </span>
            ) : (
              <span className="flex items-center gap-2">
                Get Started
                <Check className="w-5 h-5" />
              </span>
            )}
          </Button>
        </div>
      </div>
      
      {/* Custom animations */}
      <style>{`
        @keyframes pulse-slow {
          0%, 100% { opacity: 0.6; transform: scale(1); }
          50% { opacity: 0.8; transform: scale(1.05); }
        }
        .animate-pulse-slow {
          animation: pulse-slow 6s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
}
